import React, {useEffect, useRef, useState} from 'react';
import {makeRequest} from "../../utils/httpRequest";
import ProductSelectAsync from "../product/ProductSelectAsync";
import CustomerSelectAsync from "../customer/CustomerSelectAsync";
import DatePicker from "react-datepicker";
import 'react-datepicker/dist/react-datepicker.css';

import {
    getCompanyOfCurrentUser,
    getCompanyClosedConfiguration,
    getProductAvailabilityFilterByQuantity,
    getProductAvailabilityPerDate,
    getProductById,
    getProductRentalPriceByProductId, getRentalById
} from "../../utils/requestUtils";
import {
    calculateDaysDifference, formatLocalDate,
    parseLocalDate
} from "../../utils/utils";
import PaymentMethodValue from "../paymentMehod/PaymentMethodValueSelect";
import Config from "../../core/config";
import {useRecoilState} from "recoil";
import {loadingAtom} from "../../atoms";
import moment from "moment";
import SearchButton from "../../core/SearchButton";
import ActionButtonsManagerView from "../common/ActionButtonsManagerView";
import ConfirmButton from "../common/ConfirmButton";
import DeleteButton from "../common/DeleteButton";
import DecimalInput from "../common/DecimalInput";
import PlusButton from "../../core/PlusButton";
import AddressSelect from "../customer/AddressSelect";
import {useNavigate} from "react-router-dom";
import Loading from "../../core/Loading";
import {UserPermission} from "../../utils/utils";
import {usePermission} from "../../core/PermissionContext";
import { showConfirm, showAlert } from '../../core/dialog';

const apiUrl = Config.apiUrl;
const RentalManager = () => {
    const { hasUserPermission, loadingPermissions } = usePermission();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(window.location.search);
    const id = searchParams.get('id');

    // New individual parameters
    const productId = searchParams.get('productId');
    const date = searchParams.get('date');
    const days = searchParams.get('days');
    const quantity = searchParams.get('quantity');
    const customerId = searchParams.get('customerId');
    
    // New array parameter for multiple rental items
    const rentalItemsParam = searchParams.get('rentalItems');
    const rentalId = searchParams.get('rentalId');
    const isBudget = searchParams.get('isBudget') === 'true';
    const convertBudgetId = searchParams.get('convertBudgetId');
    
    const [rental, setRental] = useState(undefined);
    const [rentalContracts, setRentalContracts] = useState(undefined);

    const [showPaymentMethodValueSelect, setShowPaymentMethodValueSelect] = useState(false);

    const [productRentalPriceMap, setProductRentalPriceMap] = useState(new Map());

    const [indexRentalItem, setIndexRentalItem] = useState(-1);
    const [changeAddressVisible, setChangeAddressVisible] = useState(false);
    const [loading, setLoading] = useRecoilState(loadingAtom);

    const newEntity = {
        id: undefined,
        customer: undefined,
        rentalDate: formatLocalDate(new Date()),
        rentalItems: [],
        paymentMethods: [],
        receivables: [],
        shippingCost: 0,
        totalValue: 0,
        details: '',
        rentalContract: undefined,
        deliveryByCompany: true,
    };


    const newRentalItem = {
        product: {id: undefined, name: ''},
        quantity: 1,
        rentalDays: 0,
        value: 0,
        total: 0,
        rentalDate: formatLocalDate(new Date()),
        returnDate: formatLocalDate(new Date())
    };
    const [formData, setFormData] = useState(newEntity);
    const [companyClosedDates, setCompanyClosedDates] = useState(new Set());
    const [companyClosedWeekdays, setCompanyClosedWeekdays] = useState(new Set());

    useEffect(() => {
        const loadClosedConfiguration = async () => {
            try {
                const response = await getCompanyClosedConfiguration();
                if (response) {
                    setCompanyClosedDates(new Set((response.dates || []).map((date) => date)));
                    setCompanyClosedWeekdays(new Set((response.weekdays || []).map((weekday) => Number(weekday))));
                }
            } catch (error) {
                console.error('Erro ao carregar configuração de dias off da empresa:', error);
            }
        };
        loadClosedConfiguration();
    }, []);

    const isCompanyClosedDate = (date) => {
        if (!date) {
            return false;
        }
        const normalizedDate = new Date(date.getTime());
        normalizedDate.setHours(0, 0, 0, 0);
        const formattedDate = formatLocalDate(normalizedDate);
        return companyClosedDates.has(formattedDate) || companyClosedWeekdays.has(normalizedDate.getDay());
    };

    const getCompanyClosedMessage = (
        deliveryDate,
        returnDate,
        {
            deliveryLabel = 'data de entrega',
            returnLabel = 'data de devolução',
            suffix = '',
            includeArticleSecond = true
        } = {}
    ) => {
        const entries = [];

        if (deliveryDate && isCompanyClosedDate(deliveryDate)) {
            entries.push(`${deliveryLabel} ${deliveryDate.toLocaleDateString('pt-BR')}`);
        }

        if (returnDate && isCompanyClosedDate(returnDate)) {
            entries.push(`${returnLabel} ${returnDate.toLocaleDateString('pt-BR')}`);
        }

        if (entries.length === 0) {
            return null;
        }

        const suffixClause = suffix ? ` ${suffix}` : '';

        if (entries.length === 1) {
            return `A ${entries[0]}${suffixClause} está configurada como dia off para a empresa.`;
        }

        const secondArticle = includeArticleSecond ? 'a ' : '';
        return `A ${entries[0]} e ${secondArticle}${entries[1]}${suffixClause} estão configuradas como dias off para a empresa.`;
    };

    const notifyIfCompanyClosed = (deliveryDate, returnDate, labels) => {
        const message = getCompanyClosedMessage(deliveryDate, returnDate, labels);
        if (message) {
            showAlert(message);
            return true;
        }
        return false;
    };

    async function fullInputsWithUrlParams() {
        if (customerId) {
            await selectCustomer(parseInt(customerId));
        }
        
        if (productId && date && days && quantity) {
            const product = await getProductById(parseInt(productId));
            const rentalDate = parseLocalDate(date);

            const newReturnDate = parseLocalDate(date);
            newReturnDate.setDate(newReturnDate.getDate() + parseInt(days));

            const productRentalPrices = await getProductRentalPriceByProductId(product.id);
            productRentalPriceMap.set(product.id, productRentalPrices);
            setProductRentalPriceMap(productRentalPriceMap);

            const rentalPrice = productRentalPrices.find(r => r.days === parseInt(days));

            const newRentalItemProp = {
                product: product,
                quantity: parseInt(quantity),
                rentalDays: parseInt(days),
                value: rentalPrice ? rentalPrice.price : 0,
                total: rentalPrice ? rentalPrice.price * parseInt(quantity) : 0,
                rentalDate: formatLocalDate(rentalDate),
                returnDate: formatLocalDate(newReturnDate)
            };

            console.log('rentalItemProp.fullInputsWithUrlParams');
            setRentalItems([newRentalItemProp, newRentalItem]);
            
            const availabilities = await getProductAvailabilityPerDate(product.id, 0, parseInt(days), parseLocalDate(formData.rentalDate), rentalDate, newReturnDate, parseInt(quantity));
            if (availabilities.length === 0) {
                showAlert("Produto indisponível: " + product.name);
                return false;
            }

            notifyIfCompanyClosed(rentalDate, newReturnDate);
        }
    }

    async function fullInputsWithRentalItemsArray() {
        if (rentalItemsParam && rentalId && date) {
            try {
                const rentalItems = JSON.parse(rentalItemsParam);
                const rental = await getRentalById(parseInt(rentalId));
                
                if (rental) {
                    await selectCustomer(rental.customer.id);
                }

                const newRentalItems = [];


                for (const item of rentalItems) {

                    const newRentalDate = parseLocalDate(date);
                    newRentalDate.setDate(newRentalDate.getDate());
                    const newRentalDays = calculateDaysDifference(parseLocalDate(item.rentalDate), parseLocalDate(item.returnDate));

                    const newReturnDate = parseLocalDate(date);
                    newReturnDate.setDate(newReturnDate.getDate() + newRentalDays);

                    const product = await getProductById(item.productId);

                    const productRentalPrices = await getProductRentalPriceByProductId(product.id);
                    productRentalPriceMap.set(product.id, productRentalPrices);
                    setProductRentalPriceMap(productRentalPriceMap);

                    const rentalPrice = productRentalPrices.find(r => r.days === item.rentalDays);

                    const newRentalItem = {
                        product: product,
                        quantity: item.quantity,
                        rentalDays: item.rentalDays,
                        value: rentalPrice ? rentalPrice.price : 0,
                        total: rentalPrice ? rentalPrice.price * item.quantity : 0,
                        rentalDate: formatLocalDate(newRentalDate),
                        returnDate: formatLocalDate(newReturnDate)
                    };

                    newRentalItems.push(newRentalItem);
                }

                newRentalItems.push(newRentalItem);
                await setRentalItems(newRentalItems);
                
                // Validate availability for all items
                for (const item of newRentalItems) {

                    if(item.product.id) {
                        const availabilities = await getProductAvailabilityPerDate(item.product.id, parseInt(rentalId), item.rentalDays, parseLocalDate(formData.rentalDate), parseLocalDate(item.rentalDate), parseLocalDate(item.returnDate), item.quantity);
                        if (availabilities.length === 0) {
                            showAlert("Produto indisponível: " + item.product.name);
                            return false;
                        }
                    }
                }
            } catch (error) {
                console.error('Error parsing rentalItems:', error);
                showAlert('Erro ao processar os itens do aluguel.');
                return false;
            }
        }
    }

    async function loadBudgetForConversion(budgetId) {
        try {
            const url = apiUrl + '/api/rentals/id=' + budgetId;
            const response = await makeRequest(url, 'GET');
            
            // Create new rental data from budget, but with current date and no ID
            const newRentalData = {
                ...response,
                id: undefined, // This will create a new rental
                code: undefined, // New code will be generated
                rentalDate: formatLocalDate(new Date()), // Current date
                isBudget: false, // This will be a real rental
                // Clear payment methods to avoid detached entity issues
                paymentMethods: response.paymentMethods ? response.paymentMethods.map(pm => ({
                    ...pm,
                    id: undefined // Clear ID to create new payment method
                })) : []
            };
            
            setFormData(newRentalData);
            
            // Load product rental prices for all items
            const productRentalPriceMap = new Map();
            for (let i = 0; i < response.rentalItems.length; i++) {
                const productId = response.rentalItems[i].product.id;
                const productRentalPrices = await getProductRentalPriceByProductId(productId);
                productRentalPriceMap.set(productId, productRentalPrices);
            }
            setProductRentalPriceMap(productRentalPriceMap);
            
            // Set rental items with current date, clear IDs, and set status to PENDING
            const updatedRentalItems = response.rentalItems.map(item => ({
                ...item,
                id: undefined, // Clear ID to create new rental item
                status: 0, // STATUS_PENDING - new rental should start as pending
                rentalDate: formatLocalDate(new Date()),
                returnDate: formatLocalDate(new Date(new Date().getTime() + (item.rentalDays * 24 * 60 * 60 * 1000)))
            }));
            
            setRentalItems([...updatedRentalItems, newRentalItem]);
            
        } catch (error) {
            console.error('Error loading budget for conversion:', error);
            showAlert('Erro ao carregar dados do orçamento para conversão.');
        }
    }

    async function makeQuery() {
        if (convertBudgetId) {
            await loadBudgetForConversion(convertBudgetId);
        } else if (id && !rental) {
            try {
                const url = apiUrl + '/api/rentals/id=' + id;
                const response = await makeRequest(url, 'GET');
                setFormData(response);
                const productRentalPriceMap = new Map();
                for (let i = 0; i < response.rentalItems.length; i++) {
                    const productId = response.rentalItems[i].product.id;
                    const productRentalPrices = await getProductRentalPriceByProductId(productId);
                    productRentalPriceMap.set(productId, productRentalPrices);
                }
                setProductRentalPriceMap(productRentalPriceMap);
                setRentalItems([...response.rentalItems,
                    newRentalItem,
                ])
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        } else {
            clearEntity();
        }

        if (productId && date && days && quantity) {
            await fullInputsWithUrlParams();
        } else if (rentalItemsParam && rentalId) {
            await fullInputsWithRentalItemsArray();
        }

        setShowPaymentMethodValueSelect(false);
    }

    async function makeCompanyQuery() {
        try {
            const response = await getCompanyOfCurrentUser();
            const rentalContracts = response.rentalContracts ? response.rentalContracts : [];
            setRentalContracts(rentalContracts);
            if (!id && rentalContracts.length > 0) {
                setFormData((prevData) => ({
                    ...prevData,
                    rentalContract: rentalContracts[0]
                }));
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }

    async function getCustomerById(id) {

        try {
            const url = apiUrl + '/api/customers/id=' + id;
            const response = await makeRequest(url, 'GET');
            return response;
        } catch (error) {
            console.error('Error fetching data:', error);
        }

    }

    useEffect(() => {
        if (formData.customer && formData.customer.highlightNote && formData.customer.note && formData.customer.note !== '') {
            showAlert('Observação do Cliente:\n' + formData.customer.note);
        }
    }, [formData.customer]);


    useEffect(() => {
        makeCompanyQuery();
    }, []);
    useEffect(() => {
        makeQuery();
    }, [id]);


    async function validate() {

        if (formData.customer === undefined || formData.customer.id === undefined) {
            showAlert(`Informe o cliente.`);
            return false;
        }

        if (rentalItems.length === 1) {
            showAlert(`Informe ao menos 1 item.`);
            return false;
        }


        if (rentalItems.find((r) => !r.product && !r.product.id && (r.rentalDays === undefined || r.rentalDays === 0))) {
            showAlert(`Informe o período.`);
            return false;
        }

        for (const rentalItem of rentalItems) {
            if (rentalItem.product && rentalItem.product.id) {
                const quantity = rentalItem.quantity ? rentalItem.quantity : 0;

                const productId = rentalItem.product.id;
                const startDate = parseLocalDate(rentalItem.rentalDate);
                const endDate = parseLocalDate(rentalItem.returnDate);
                const days = calculateDaysDifference(startDate, endDate);

                const availabilities = await getProductAvailabilityPerDate(productId, getRentalIds(), days, parseLocalDate(formData.rentalDate), startDate, endDate, quantity);
                if (availabilities.length === 0) {
                    if (!await showConfirm("Produto indisponível: " + rentalItem.product.name + "\nDeseja realmente alugar para essa data?")) {
                        return false;
                    }
                }
            }
        }

        return true;
    }

    const onConfirm = async () => {
        const warnings = new Set();

        rentalItems.slice(0, -1).forEach((item, index) => {
            if (item && item.product && item.product.id) {
                const deliveryDate = parseLocalDate(item.rentalDate);
                const returnDate = parseLocalDate(item.returnDate);
                const contextSuffix = item.product.name
                    ? `do produto ${item.product.name}`
                    : `do item ${index + 1}`;

                const message = getCompanyClosedMessage(deliveryDate, returnDate, {
                    deliveryLabel: `data de entrega`,
                    returnLabel: `data de devolução`,
                    suffix: contextSuffix,
                    includeArticleSecond: false
                });

                if (message) {
                    warnings.add(message);
                }
            }
        });

        if (warnings.size > 0) {
            const combinedMessage = Array.from(warnings).join('\n');
            if (!await showConfirm(`${combinedMessage}\n\nDeseja continuar mesmo assim?`)) {
                return;
            }
        }

        if (await validate()) {
            if (formData.totalValue) {
                openPaymentMethodValueSelect();
            } else {
                if (await showConfirm("O valor do aluguel está zerado. Deseja realmente confirmar?")) {
                    confirmRental([]);
                }
            }
        }
    };

    async function saveEntity(paymentMethodValues) {
        const rental = {

            id: formData.id,
            code: formData.code,
            customer: formData.customer,
            rentalItems: rentalItems.slice(0, -1),
            rentalDate: formData.rentalDate,
            shippingCost: formData.shippingCost ? formData.shippingCost : 0,
            details: formData.details,
            paymentMethods: paymentMethodValues,
            totalValue: formData.totalValue,
            address: formData.address,
            addressNumber: formData.addressNumber,
            addressComplement: formData.addressComplement,
            neighborhood: formData.neighborhood,
            cep: formData.cep,
            city: formData.city,
            state: formData.state,
            rentalContract: formData.rentalContract,
            deliveryByCompany: formData.deliveryByCompany,
            isBudget: isBudget,
            convertBudgetId: convertBudgetId,

        };
        
        let url;
        if (isBudget && !convertBudgetId) {
            url = apiUrl + '/api/rentals/budgets';
        } else {
            url = apiUrl + '/api/rentals';
        }
        
        const response = await makeRequest(url, id ? 'PUT' : 'POST', {
            'Content-Type': 'application/json',
        }, JSON.stringify(rental));
        return response.id;
    }

    function clearEntity() {
        setFormData(newEntity);
        setRentalItems([newRentalItem]);
    }

    const confirmRental = async (paymentMethodValues) => {
        try {
            setLoading(true);
            const newRentalId = await saveEntity(paymentMethodValues);

            if (rentalId) {
                const oldRentalItems = JSON.parse(rentalItemsParam);

                console.log('oldRentalItems', oldRentalItems)
                const oldProductIds = oldRentalItems.map(ri => ri.productId).join(',');
                const productIds = rentalItems.slice(0, -1).map(ri => ri.product.id).join(',');

                let url = apiUrl + '/api/rentals/finishRentalItems?id=' + rentalId + '&productIds=' + oldProductIds;
                await makeRequest(url, 'PUT', {
                    'Content-Type': 'application/json',
                });

                url = apiUrl + '/api/rentals/deliverRentalItems?id=' + newRentalId + '&productIds=' + productIds;
                await makeRequest(url, 'PUT', {
                    'Content-Type': 'application/json',
                });
            }

            if (convertBudgetId) {
                navigate('/app/rentallist');
            } else {
                navigate(isBudget ? '/app/rentallist?isBudget=true' : '/app/rentallist');
            }
        } catch (error) {
            console.error('Error saving:', error);
            showAlert('Erro ao salvar.');
        } finally {
            setLoading(false);
        }
    };

    async function selectCustomer(id) {
        const customer = await getCustomerById(id);
        setFormData((prevData) => {
            const newFormData = {
                ...prevData,
                customer: customer
            };

            if (prevData.customer?.id !== customer?.id) {
                newFormData.address = customer.address;
                newFormData.addressNumber = customer.addressNumber;
                newFormData.addressComplement = customer.addressComplement;
                newFormData.neighborhood = customer.neighborhood;
                newFormData.cep = customer.cep;
                newFormData.city = customer.city;
                newFormData.state = customer.state;
            }

            return newFormData;
        });
    }

    const handleCustomerItemClick = async (id) => {
        await selectCustomer(id);

    };


    function getRentalIds() {
        return [formData.id, rentalId].filter(value => value !== 0 && value !== undefined).join(',');
    }

    const appendEmptyRentalRow = (items, indexRentalItem) => {
        if (indexRentalItem !== items.length - 1) {
            return items;
        }

        const nextRentalDate = new Date(parseLocalDate(formData.rentalDate).getTime());
        nextRentalDate.setDate(nextRentalDate.getDate() + 1);

        return [...items, {
            product: {id: undefined, name: ''},
            quantity: 1,
            rentalDays: 0,
            value: 0,
            total: 0,
            rentalDate: formatLocalDate(nextRentalDate),
            returnDate: formatLocalDate(new Date()),
        }];
    };

    async function selectProduct(productId, indexRentalItem, rentalDateParam, rentalDaysParam, quantityParam) {
        const product = await getProductById(productId);
        const productRentalPrices = await getProductRentalPriceByProductId(productId);

        setProductRentalPriceMap((prev) => {
            const next = new Map(prev);
            next.set(productId, productRentalPrices);
            return next;
        });

        const rentalDays = rentalDaysParam ?? (productRentalPrices.length > 0 ? productRentalPrices[0].days : 0);
        const quantity = quantityParam ?? rentalItems[indexRentalItem].quantity;
        const rentalPriceEntry = productRentalPrices?.find((p) => p.days === rentalDays) ?? productRentalPrices?.[0];
        const unitPrice = rentalPriceEntry?.price ?? 0;

        if (rentalDateParam && rentalDays > 0) {
            const deliveryDate = new Date(rentalDateParam.getTime());
            const returnDate = new Date(deliveryDate.getTime());
            returnDate.setDate(returnDate.getDate() + rentalDays);

            setRentalItems((prevItems) => {
                const updated = prevItems.map((item, i) => i === indexRentalItem ? {
                    ...item,
                    product: {id: product.id, name: product.name, code: product.code},
                    quantity,
                    rentalDays,
                    value: unitPrice,
                    total: unitPrice * quantity,
                    rentalDate: formatLocalDate(deliveryDate),
                    returnDate: formatLocalDate(returnDate),
                } : item);

                return appendEmptyRentalRow(updated, indexRentalItem);
            });

            const availabilities = await getProductAvailabilityPerDate(
                productId,
                getRentalIds(),
                rentalDays,
                parseLocalDate(formData.rentalDate),
                deliveryDate,
                returnDate,
                quantity,
            );

            if (availabilities.length === 0) {
                showAlert('Produto indisponível: ' + product.name);
                return;
            }

            calculateTotal(indexRentalItem);
            notifyIfCompanyClosed(deliveryDate, returnDate, {
                deliveryLabel: 'data de entrega',
                returnLabel: 'data de devolução',
            });
            return;
        }

        const rentalDate = new Date(parseLocalDate(formData.rentalDate).getTime());
        rentalDate.setDate(rentalDate.getDate() + 1);

        const availabilities = await getProductAvailabilityFilterByQuantity(
            productId,
            getRentalIds(),
            rentalDate,
            rentalDays,
            1,
        );

        if (availabilities.length > 0) {
            const chosenDate = parseLocalDate(availabilities[0].startDate);
            const returnDate = new Date(chosenDate.getTime());
            returnDate.setDate(returnDate.getDate() + rentalDays);

            setRentalItems((prevItems) => {
                const updated = prevItems.map((item, i) => i === indexRentalItem ? {
                    ...item,
                    product: {id: product.id, name: product.name, code: product.code},
                    quantity: 1,
                    rentalDays,
                    value: unitPrice,
                    total: unitPrice,
                    rentalDate: formatLocalDate(chosenDate),
                    returnDate: formatLocalDate(returnDate),
                } : item);
                return appendEmptyRentalRow(updated, indexRentalItem);
            });

            calculateTotal(indexRentalItem);
            notifyIfCompanyClosed(chosenDate, returnDate, {
                deliveryLabel: 'data de entrega',
                returnLabel: 'data de devolução',
            });
        } else {
            showAlert('Produto indisponível: ' + product.name);
        }
    }

    const handleProductItemClick = async (id, index) => {
        setLoading(true);
        if (index === rentalItems.length - 1) {
            // Clear the ProductSelectAsync value for the last item
            // The selectProduct function will add a new empty item, which will clear the current selection
            // The new item will have product: {id: undefined, name: ''} which will show as null value
        }
        await selectProduct(id, index);
        setLoading(false);
    };

    const [rentalItems, setRentalItems] = useState([
        newRentalItem,
    ]);

    useEffect(() => {
        let total = parseFloat(formData.shippingCost ? formData.shippingCost : 0);
        rentalItems.forEach(i => total += i.total);
        setFormData((prevData) => ({
            ...prevData,
            ['totalValue']: total
        }));

    }, [rentalItems, formData.shippingCost]);


    useEffect(() => {
        console.log('rentalItems' , rentalItems)
    }, [rentalItems]);
    // Função para processar a exclusão do produto
    const removeItem = (index) => {
        const novaLista = [...rentalItems];
        novaLista.splice(index, 1);
        setRentalItems(novaLista);
    };


    async function handleRentalDateItemChange(index, date, rentalDays) {

        if(!date) {
            return;
        }
        const differenceDays = calculateDaysDifference((parseLocalDate(formData.rentalDate)), date);
        if (differenceDays < 0) {
            showAlert('Data do aluguel não pode ser anterior à data do lançamento.');
            return;
        }
        rentalDays = (rentalDays ? rentalDays : rentalItems[index].rentalDays);
        const newReturnDate = new Date(date.getTime());
        newReturnDate.setDate(newReturnDate.getDate() + rentalDays);

        console.log('rentalItems.handleRentalDateItemChange')
        setRentalItems((prevItems) =>
            prevItems.map((item, i) =>
                i === index ? {
                    ...item,
                    rentalDate: formatLocalDate(date),
                    returnDate: formatLocalDate(newReturnDate)
                } : item
            )
        );

        calculateTotal(index);
        const quantity = rentalItems[index].quantity;

        const product = rentalItems[index].product;

        const availabilities = await getProductAvailabilityPerDate(product.id, getRentalIds(), rentalDays, parseLocalDate(formData.rentalDate), new Date(date), newReturnDate, quantity)
        if (availabilities.length === 0) {
            showAlert("Produto indisponível: " + product.name);
            return;
        }

        notifyIfCompanyClosed(date, newReturnDate, {
            deliveryLabel: 'data de entrega',
            returnLabel: 'data de devolução'
        });

    }

    async function handleReturnDateItemChange(index, returnDate) {

        if(!returnDate) {
            return;
        }
        const rentalDate = parseLocalDate(rentalItems[index].rentalDate);
        const differenceDays = calculateDaysDifference((rentalDate), returnDate);
        if (differenceDays < 0) {
            showAlert('Data da devolução não pode ser anterior à data da entrega.');
            return;
        }


        const productId = rentalItems[index].product.id;
        const productRentalPrices = productRentalPriceMap.get(productId);
        const productRentalPrice = productRentalPrices.find((p) => p.days >= differenceDays);

        console.log('rentalItems.handleReturnDateItemChange')
        setRentalItems((prevItems) =>
            prevItems.map((item, i) =>
                i === index ? {
                    ...item,
                    rentalDays: productRentalPrice ? productRentalPrice.days : item.rentalDays,
                    value: productRentalPrice ? productRentalPrice.price : item.value,
                    total: productRentalPrice ? productRentalPrice.price * item.quantity : item.value,
                    returnDate: formatLocalDate(returnDate)
                } : item
            )
        );

        calculateTotal(index);
        const quantity = rentalItems[index].quantity;


        const availabilities = await getProductAvailabilityPerDate(productId, getRentalIds(), differenceDays, parseLocalDate(formData.rentalDate), parseLocalDate(rentalDate), returnDate, quantity)
        if (availabilities.length === 0) {
            showAlert('Produto indisponível.')
            return;
        }

        notifyIfCompanyClosed(rentalDate, returnDate, {
            deliveryLabel: 'data de entrega',
            returnLabel: 'data de devolução'
        });

    }

    async function handleQuantityChange(index, quantity) {
        console.log('rentalItems.handleQuantityChange');
        await setRentalItems((prevItems) =>
            prevItems.map((item, i) =>
                i === index ? {
                    ...item,
                    quantity:
                    quantity
                } : item
            )
        );

        calculateTotal(index);


        if (quantity > 0) {
            const productId = rentalItems[index].product.id;
            const days = rentalItems[index].rentalDays;
            const rentalDate = new Date(parseLocalDate(formData.rentalDate).getTime());

            rentalDate.setDate(rentalDate.getDate() + 1);
            const availabilities = await getProductAvailabilityFilterByQuantity(productId, getRentalIds(), rentalDate, days, quantity);
            if (availabilities.length > 0) {
                console.log('rentalItems.handleQuantityChange');
                setRentalItems((prevItems) =>
                    prevItems.map((item, i) =>
                        i === index ? {
                            ...item,
                            rentalDate: availabilities[0].startDate
                        } : item
                    )
                );

            } else {
                showAlert('Produto indisponível.')
                return;
            }
        }

    }

    function handleValueChange(index, value) {
        console.log('rentalItems.handleValueChange');
        setRentalItems((prevItems) =>
            prevItems.map((item, i) =>
                i === index ? {
                    ...item,
                    value:
                        value ? value : 0,
                } : item
            )
        );

        calculateTotal(index);

    }

    function calculateTotal(index) {
        console.log('rentalItems.calculateTotal');
        setRentalItems((prevItems) =>
            prevItems.map((item, i) =>
                i === index ? {
                    ...item,
                    total:
                        (item.quantity ? item.quantity : 0) * (item.value ? item.value : 0),
                } : item
            )
        );
    }

    const handleDateChange = (date) => {
        if (date) {
            notifyIfCompanyClosed(date, null, {deliveryLabel: 'data de lançamento'});
            setFormData((prevData) => ({
                ...prevData,
                ['rentalDate']: formatLocalDate(date)
            }));
        }
    };

    const openPaymentMethodValueSelect = () => {
        setShowPaymentMethodValueSelect(true);
    };

    const closePaymentMethodValueSelect = () => {
        setShowPaymentMethodValueSelect(false);
    };

    async function handleRentalContractSelected(rentalContractId) {
        const rentalContract = rentalContracts.find(rc => rc.id === Number(rentalContractId));
        if (rentalContract) {
            setFormData((prevData) => ({
                ...prevData,
                rentalContract: rentalContract
            }));
        } else {
            setFormData((prevData) => ({
                ...prevData,
                rentalContract: undefined
            }));
        }
    }

    async function handleRentalPriceSelected(index, value) {
        const rentalPrices = productRentalPriceMap.get(rentalItems[index].product.id);
        const rentalPrice = rentalPrices.find(r => r.days === Number(value));


        const productId = rentalItems[index].product.id;
        const quantity = rentalItems[index].quantity;
        const rentalDate = new Date(parseLocalDate(formData.rentalDate).getTime());
        rentalDate.setDate(rentalDate.getDate() + 1);
        const newReturnDate = parseLocalDate(formData.rentalDate);
        const days = value ? Number(value) : 0;
        newReturnDate.setDate(newReturnDate.getDate() + days);

        const availabilities = await getProductAvailabilityFilterByQuantity(productId, getRentalIds(), rentalDate, days, quantity);
        if (availabilities.length > 0) {
            console.log('rentalItems.handleRentalPriceSelected');
            setRentalItems((prevItems) =>
                prevItems.map((item, i) => {
                        if (i === index) {
                            const rentalDate = availabilities[0].startDate;
                            const newReturnDateStr = availabilities[0].startDate;
                            let newReturnDate = parseLocalDate(newReturnDateStr);
                            newReturnDate.setDate(newReturnDate.getDate() + days);
                            return {
                                ...item,
                                rentalDays: days,
                                value: rentalPrice ? rentalPrice.price : 0,
                                rentalDate: rentalDate,
                                returnDate: formatLocalDate(newReturnDate)
                            };
                        } else {
                            return item
                        }
                    }
                )
            );

        } else {
            showAlert('Produto indisponível.')
            return;
        }

        calculateTotal(index);
    }

    const handleChange = (e) => {
        const {name, value, type, checked} = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const onChangeAddress = (formDataParam) => {

        formData.address = formDataParam.address;
        formData.addressNumber = formDataParam.addressNumber;
        formData.addressComplement = formDataParam.addressComplement;
        formData.neighborhood = formDataParam.neighborhood;
        formData.cep = formDataParam.cep;
        formData.city = formDataParam.city;
        formData.state = formDataParam.state;

        setFormData(formData);
        setChangeAddressVisible(false);
    }

    // Add this handler for product selection via calendar
    const handleRequestRentProduct = async (productId, date, days, quantity, indexRentalItem) => {
        if (indexRentalItem === null || indexRentalItem === undefined) {
            console.error('indexRentalItem is required for handleRequestRentProduct');
            return;
        }
        setLoading(true);
        try {
            await selectProduct(productId, indexRentalItem, date, days, quantity);
        } finally {
            setLoading(false);
        }
    };

    if (loadingPermissions) {
        return <Loading></Loading>;
    }
    if (id && hasUserPermission(UserPermission.EDIT_RENTAL) === false) {
        navigate('/unauthorizedaccess');
        return null;
    }
    if (!id && hasUserPermission(UserPermission.CREATE_RENTAL) === false) {
        navigate('/unauthorizedaccess');
        return null;
    }

    return (
        <div className="app-form-page">
            {changeAddressVisible && <AddressSelect
                onConfirm={onChangeAddress}
                onClose={() => setChangeAddressVisible(false)}
                address={formData.address ? formData.address : formData.customer.address}
                addressNumber={formData.address ? formData.addressNumber : formData.customer.addressNumber}
                addressComplement={formData.address ? formData.addressComplement : formData.customer.addressComplement}
                neighborhood={formData.address ? formData.neighborhood : formData.customer.neighborhood}
                cep={formData.address ? formData.cep : formData.customer.cep}
                city={formData.address ? formData.city : formData.customer.city}
                state={formData.address ? formData.state : formData.customer.state}

            />}
            {showPaymentMethodValueSelect && (
                <PaymentMethodValue onItemClick={confirmRental} onClose={closePaymentMethodValueSelect}
                                    paymentMethods={formData.paymentMethods}
                                    considerPaidAccordingToDueDate={false}
                                    totalValue={formData.totalValue} onConfirm={(paymentMethodValues) => {
                    closePaymentMethodValueSelect();
                    confirmRental(paymentMethodValues);
                }}/>)}
            <div
                className="header-label">
                {convertBudgetId ? 'Converter Orçamento em Aluguel' : 
                 (formData.id > 0 ? 'Editar' : 'Novo') + ' ' + (isBudget ? 'Orçamento' : 'Aluguel') + (formData.id > 0 ? ' - ' + formData.code : '')}
            </div>
            {/*{formData.id && (*/}
            {/*    <div style={{marginTop: '5px', alignItems: 'center'}}>*/}
            {/*        <div>Código:</div>*/}
            {/*        <input type="text" style={{backgroundColor: '#CCCCCC'}} value={formData.id} disabled={true}/>*/}
            {/*    </div>)}*/}

            <div style={{display: "flex"}}>
                <div className="rental-form-customer-row" style={{marginTop: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Cliente:</div>
                    <div className="customer-select-field">
                        <CustomerSelectAsync
                            onItemClick={handleCustomerItemClick}
                            prefilledCustomer={formData.customer}
                            dataTest="customer-async-select"
                        />
                    </div>
                </div>
                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div>Data:</div>
                    <DatePicker
                        selected={parseLocalDate(formData.rentalDate)}
                        onChange={handleDateChange}
                        dateFormat="dd/MM/yyyy"
                        autoComplete="off"
                        className="datepicker"
                    />
                </div>
                {formData.customer &&
                    <>
                        <div className="rental-form-address-row" style={{marginTop: '5px', marginLeft: '5px', alignItems: 'flex-start'}}>
                            <div>Endereço:</div>
                            <div
                                className="rental-form-address-row__content"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: '8px',
                                    maxWidth: '425px',
                                }}
                            >
                                <div
                                    className="rental-form-address-row__text"
                                    style={{
                                        flex: '1 1 auto',
                                        minWidth: 0,
                                        fontSize: '14px',
                                        overflowWrap: 'break-word',
                                    }}
                                >
                                    {formData.address !== undefined && (formData.address +
                                        (formData.addressNumber !== null && formData.addressNumber > 0 ?
                                            (', ' + formData.addressNumber) : '') +
                                        (formData.addressComplement !== null && formData.addressComplement !== undefined && formData.addressComplement !== '' ? ' - ' + formData.addressComplement : '') +
                                        ' - ' + formData.neighborhood)}
                                </div>
                                <button type="button" onClick={() => setChangeAddressVisible(true)}>Endereço</button>
                            </div>
                        </div>
                    </>
                }
            </div>

            {rentalContracts && rentalContracts.length > 1 &&
                <div style={{marginTop: '5px', alignItems: 'center'}}>
                    <div style={{marginTop: '5px', alignItems: 'center'}}>
                        <div style={{width: '180px'}}>Contrato:</div>
                    </div>
                    <select value={formData?.rentalContract?.id}
                            onChange={(e) => handleRentalContractSelected(e.target.value)}>
                        {rentalContracts.map((rentalContract, index) => (
                            <option key={index} value={rentalContract.id}>
                                {rentalContract.name}
                            </option>
                        ))}
                    </select>
                </div>
            }
            <div style={{marginTop: '5px', alignItems: 'center'}}>


                <table style={{width: '1000px', height: (rentalContracts?.length <= 1 ? '351px' : '295px')}}>

                    <colgroup>
                        <col style={{width: '450px'}}/>
                        <col style={{width: '50px'}}/>
                        <col style={{width: '50px'}}/>
                        <col style={{width: '50px'}}/>
                        <col style={{width: '50px'}}/>
                        <col style={{width: '50px'}}/>
                        <col style={{width: '50px'}}/>
                        <col style={{width: '50px'}}/>
                    </colgroup>
                    <thead>
                    <tr>
                        <th>Produto</th>
                        <th>Quantidade</th>
                        <th>Período</th>
                        <th>Entrega</th>
                        <th>Devolução</th>
                        <th>Valor (R$)</th>
                        <th>Total (R$)</th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody style={{overflowY: 'auto'}}>
                    {rentalItems.map((item, index) => (
                        <>
                            {(
                                <tr key={item.product.id + index}>
                                    <td>
                                        <div style={{display: 'flex', alignItems: 'center', width: '200px'}}>
                                            <ProductSelectAsync
                                                showProductAvailability={true}
                                                style={{width: '300px', menuWidth: '100%', menuMinWidth: 'min(420px, 100vw - 24px)'}}
                                                value={item.product && item.product.id ? item.product.id : null}
                                                onItemClick={(id) => handleProductItemClick(id, index)}
                                                onRequestRentProduct={handleRequestRentProduct}
                                                prefilledProduct={item.product && item.product.id ? item.product : null}
                                                index={index}
                                                dataTest={`product-async-select-${index}`}
                                            />
                                        </div>
                                    </td>
                                    {index < rentalItems.length - 1 && (
                                        <>
                                            <td>
                                                <input
                                                    name={`quantity-${index}`}
                                                    style={{width: '70px'}}
                                                    key={item.id}
                                                    type="number"
                                                    step="1"
                                                    placeholder=""
                                                    value={item.quantity}
                                                    onChange={(e) => handleQuantityChange(index, parseFloat(e.target.value))}
                                                />
                                            </td>
                                            <td>
                                                <select
                                                    name={`rentalDays-${index}`}
                                                    style={{width: '100px'}}
                                                    value={item.rentalDays}
                                                    onChange={(e) => handleRentalPriceSelected(index, e.target.value)}
                                                >
                                                    <option value="">Selecione um período</option>
                                                    {productRentalPriceMap.get(item.product.id) &&
                                                        productRentalPriceMap.get(item.product.id).map((rentalPrice, i) => (
                                                            <option key={i} value={rentalPrice.days}>
                                                                {rentalPrice.days} dias
                                                            </option>
                                                        ))}
                                                </select>
                                            </td>
                                            <td>
                                                <DatePicker
                                                    name={`rentalDate-${index}`}
                                                    selected={parseLocalDate(item.rentalDate)}
                                                    onChange={(date) => handleRentalDateItemChange(index, date)}
                                                    dateFormat="dd/MM/yyyy"
                                                    autoComplete="off"
                                                    className="datepicker"
                                                />
                                            </td>
                                            <td>
                                                <DatePicker
                                                    name={`returnDate-${index}`}
                                                    selected={parseLocalDate(item.returnDate)}
                                                    onChange={(date) => handleReturnDateItemChange(index, date)}
                                                    dateFormat="dd/MM/yyyy"
                                                    autoComplete="off"
                                                    className="datepicker"
                                                />
                                            </td>
                                            <td>
                                                <DecimalInput
                                                    name={`rental-item-value-${index}`}
                                                    style={{width: '100px', textAlign: 'right'}}
                                                    key={item.id}
                                                    placeholder="0,00"
                                                    value={item.value}
                                                    onChange={(e) => handleValueChange(index, e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <div style={{width: '70px', textAlign: 'right'}}>
                                                    {item.total.toLocaleString('pt-BR', {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2
                                                    })}
                                                </div>
                                            </td>
                                            <td>
                                                {index < rentalItems.length - 1 && (
                                                    <DeleteButton onClick={async () => {
                                                        if (await showConfirm('Deseja realmente excluir este item?')) {
                                                            removeItem(index);
                                                        }
                                                    }}/>
                                                )}
                                            </td>
                                        </>
                                    )}
                                </tr>
                            )}
                        </>
                    ))}
                    </tbody>
                </table>

            </div>

            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', width: '100%'}}>
                <div style={{marginTop: '5px'}}>
                    <div style={{height: '16px'}}/>
                    <div style={{height: '38px', display: 'flex', alignItems: 'center'}}>
                        <input name="deliveryByCompany" type="checkbox" checked={formData.deliveryByCompany}
                               onChange={handleChange}/>
                        <div>
                            Entregue pela empresa?
                        </div>
                    </div>
                </div>
                <div style={{marginTop: '5px', display: 'flex', flexDirection: 'column'}}>
                    <div>Frete:</div>
                    <DecimalInput
                        style={{width: '100px'}}
                        placeholder="0,00"
                        value={formData.shippingCost}
                        name="shippingCost"
                        onChange={handleChange}
                    />
                </div>

                <div style={{
                    marginTop: '5px',
                    marginLeft: '5px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    flexGrow: 1
                }}>
                    <div style={{width: '100%'}}>Observação:</div>
                    <input type="text" style={{width: '100%'}} value={formData.details} name="details"
                           onChange={handleChange}/>
                </div>

                <div style={{
                    marginLeft: '5px',
                    marginTop: '5px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}>
                    <div style={{height: '17.6px'}}/>
                </div>
            </div>
            <div className="total-label"
                 style={{height: '37.6px', display: 'flex', alignItems: "center", justifyContent: 'flex-end', width: '100%'}}
                 data-test={"total-value"}>
                Valor Total: R$ {formData.totalValue.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })}
            </div>


            <ActionButtonsManagerView>
                <ConfirmButton onClick={onConfirm}/>
            </ActionButtonsManagerView>

        </div>
    );
};

export default RentalManager;
